import downloadIcon from "../assets/res/download_icon.svg";

// ── Offer card used in the "You May Like" horizontal scroll ──
export const OfferCard = ({ offer, onPress }) => (
  <div className="inline-block" onClick={() => onPress(offer)}>
    <div className="relative h-[120px] w-[170px] overflow-hidden rounded-lg shadow-md bg-white hover:shadow-xl transition-shadow duration-300 ease-in-out cursor-pointer">
      <img
        className="h-[120px] w-[170px] object-cover object-center rounded-lg"
        src={offer.picture}
        alt={offer.name_short}
      />
      <div className="absolute top-2 left-2 bg-green-500 text-white text-[10px] semiBold px-2 py-0.5 rounded-full">
        Free
      </div>
      <div className="absolute bottom-0 left-0 flex h-[60px] flex-row z-10 w-full backdrop-blur-2xl bg-light-blue/30">
        <div className="flex flex-col p-2 w-3/4">
          <p className="text-[16px] medium text-white tracking-wider leading-none">
            {offer.name_short}
          </p>
        </div>
        <div className="w-1/4 flex flex-col justify-center items-end mr-2">
          <div className="rounded-full bg-dark-blue flex items-center justify-center h-[36px] w-[36px]">
            <span className="text-white text-xs semiBold">Go</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ── Bottom sheet shown when user taps a "You May Like" card ──
const OfferBottomSheet = ({ offer, onClose, onProceed }) => {
  if (!offer) return null;

  const handleRedirect = () => {
    window.open(offer.link, "_blank");
    onProceed?.();
    onClose();
  };

  return (
    <>
      <style>{`
        @keyframes sheetSlideUp {
          from { transform: translateX(-50%) translateY(100%); opacity: 0; }
          to   { transform: translateX(-50%) translateY(0);   opacity: 1; }
        }
        @keyframes ctaPulse {
          0%, 100% { box-shadow: 0 0 18px rgba(70,143,175,0.45), 0 4px 20px rgba(1,73,124,0.55); }
          50%       { box-shadow: 0 0 32px rgba(70,143,175,0.75), 0 4px 32px rgba(1,73,124,0.75); }
        }
        @keyframes backdropIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[55]"
        style={{
          background: "rgba(0,10,22,0.75)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          animation: "backdropIn 0.25s ease forwards",
        }}
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className="fixed bottom-0 left-1/2 w-full max-w-md md:max-w-lg z-[60]"
        style={{
          animation: "sheetSlideUp 0.38s cubic-bezier(0.32, 0.72, 0, 1) forwards",
        }}
      >
        <div
          className="relative overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #010f1e 0%, #012a4a 45%, #013a63 100%)",
            borderRadius: "28px 28px 0 0",
            border: "1px solid rgba(70,143,175,0.18)",
            borderBottom: "none",
            boxShadow: "0 -8px 48px rgba(0,0,0,0.7)",
          }}
        >
          {/* Hero image — full bleed, fades into sheet */}
          <div className="relative w-full overflow-hidden" style={{ height: "220px" }}>
            <img
              src={offer.picture}
              alt={offer.name_short}
              className="w-full h-full object-cover"
              style={{ objectPosition: "center top" }}
            />
            {/* Gradient fade into sheet background */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(1,15,30,0) 25%, rgba(1,15,30,0.55) 60%, #010f1e 100%)",
              }}
            />
            {/* LEFT edge vignette */}
            <div
              className="absolute inset-y-0 left-0 w-8"
              style={{ background: "linear-gradient(to right, rgba(1,15,30,0.4), transparent)" }}
            />
            {/* RIGHT edge vignette */}
            <div
              className="absolute inset-y-0 right-0 w-8"
              style={{ background: "linear-gradient(to left, rgba(1,15,30,0.4), transparent)" }}
            />

            {/* FREE badge — pressStart pixel style */}
            <div
              className="absolute top-4 left-4 pressStart text-white"
              style={{
                fontSize: "8px",
                letterSpacing: "0.1em",
                padding: "6px 10px",
                borderRadius: "6px",
                background: "linear-gradient(135deg, #16a34a, #15803d)",
                border: "1px solid rgba(255,255,255,0.25)",
                boxShadow: "0 2px 14px rgba(22,163,74,0.55)",
              }}
            >
              FREE
            </div>

            {/* Drag handle — sits on the image */}
            <div className="absolute bottom-3 left-0 right-0 flex justify-center">
              <div
                className="w-10 h-1 rounded-full"
                style={{ background: "rgba(255,255,255,0.28)" }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="px-5 pb-8 pt-4">
            {/* Title */}
            <h2
              className="semiBold text-white leading-tight"
              style={{ fontSize: "26px", letterSpacing: "0.01em" }}
            >
              {offer.name_short}
            </h2>

            {/* Accent divider */}
            <div
              className="mt-3 mb-3"
              style={{
                height: "1px",
                background:
                  "linear-gradient(90deg, rgba(70,143,175,0.5) 0%, rgba(70,143,175,0.1) 60%, transparent 100%)",
              }}
            />

            {/* Ad copy */}
            <p
              className="medium leading-relaxed"
              style={{ color: "rgba(255,255,255,0.52)", fontSize: "14px" }}
            >
              {offer.adcopy}
            </p>

            {/* CTA button */}
            <button
              onClick={handleRedirect}
              className="w-full mt-6 semiBold text-white relative overflow-hidden"
              style={{
                padding: "17px",
                borderRadius: "16px",
                fontSize: "16px",
                letterSpacing: "0.06em",
                background: "linear-gradient(135deg, #01497c 0%, #468faf 100%)",
                border: "1px solid rgba(70,143,175,0.5)",
                animation: "ctaPulse 2.8s ease-in-out infinite",
              }}
            >
              {/* Shine sweep */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.12) 50%, transparent 65%)",
                }}
              />
              <span className="relative z-10">Get it Free →</span>
            </button>

            {/* Dismiss */}
            <button
              onClick={onClose}
              className="w-full mt-3 medium"
              style={{
                padding: "11px",
                fontSize: "13px",
                color: "rgba(255,255,255,0.28)",
                letterSpacing: "0.02em",
              }}
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OfferBottomSheet;
